import { useEffect, useState } from 'react'
import Papa from 'papaparse'

const NISOLO_CSV_URL = 'https://cdn.magicpatterns.com/uploads/ibMoCgMquYCRNyivaURNsc/nisolo_product_live_with_image_url.csv'

export type NisoloProduct = {
  handle: string
  title: string
  type: string
  status: string
  image: string
  variants: number
  units: number
  price: number
  msrp: number
  sizes: string[]
}

export type Breakdown = { label: string; units: number }

export type NisoloInventory = {
  products: NisoloProduct[]
  totalProducts: number
  liveProducts: number
  totalUnits: number
  liveUnits: number
  totalMsrp: number
  totalRetail: number
  categories: Breakdown[]
  sizes: Breakdown[]
  variantRows: number
  withImages: number
}

type CsvRow = Record<string, string>

const IMAGE_PATTERN = /https?:\/\/[^\s"'<>]+?\.(?:jpg|jpeg|png|webp|avif|gif)(?:\?[^\s"'<>]*)?/i

function findImage(row: CsvRow) {
  for (const key of ['Image Src', 'Variant Image']) {
    const match = String(row[key] ?? '').match(IMAGE_PATTERN)
    if (match) return match[0]
  }
  return ''
}

const toNumber = (value: string | undefined) => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

function buildInventory(rows: CsvRow[]): NisoloInventory {
  const byHandle = new Map<string, NisoloProduct>()
  const categoryUnits = new Map<string, number>()
  const sizeUnits = new Map<string, number>()
  let variantRows = 0

  rows.forEach((row) => {
    const handle = (row['Handle'] ?? '').trim()
    if (!handle) return

    const qty = toNumber(row['Variant Inventory Qty'])
    const price = toNumber(row['Variant Price'])
    const compareAt = toNumber(row['Variant Compare At Price'])
    const unitMsrp = compareAt > price ? compareAt : price
    const size = (row['Option1 Value'] ?? '').trim()
    const image = findImage(row)

    const isVariantRow = Boolean((row['Variant ID'] ?? '').trim())
    if (isVariantRow) {
      variantRows += 1
      const category = (row['Type'] ?? '').trim() || 'Uncategorized'
      categoryUnits.set(category, (categoryUnits.get(category) ?? 0) + qty)
      if (size) sizeUnits.set(size, (sizeUnits.get(size) ?? 0) + qty)
    }

    const existing = byHandle.get(handle)
    if (existing) {
      if (!existing.image && image) existing.image = image
      if (!existing.title && row['Title']) existing.title = row['Title'].trim()
      if (!existing.type && row['Type']) existing.type = row['Type'].trim()
      if (!existing.status && row['Status']) existing.status = row['Status'].trim()
      if (!isVariantRow) return

      existing.variants += 1
      existing.units += qty
      existing.price = existing.price || price
      existing.msrp += qty * unitMsrp
      if (size && !existing.sizes.includes(size)) existing.sizes.push(size)
      return
    }

    byHandle.set(handle, {
      handle,
      title: (row['Title'] ?? handle).trim(),
      type: (row['Type'] ?? '').trim(),
      status: (row['Status'] ?? '').trim(),
      image,
      variants: isVariantRow ? 1 : 0,
      units: isVariantRow ? qty : 0,
      price: isVariantRow ? price : 0,
      msrp: isVariantRow ? qty * unitMsrp : 0,
      sizes: isVariantRow && size ? [size] : [],
    })
  })

  const products = Array.from(byHandle.values()).sort((a, b) => b.units - a.units)
  const isLive = (product: NisoloProduct) => product.status.toLocaleLowerCase() === 'active' && product.units > 0

  return {
    products,
    totalProducts: products.length,
    liveProducts: products.filter(isLive).length,
    totalUnits: products.reduce((sum, product) => sum + product.units, 0),
    liveUnits: products.filter(isLive).reduce((sum, product) => sum + product.units, 0),
    totalMsrp: products.reduce((sum, product) => sum + product.msrp, 0),
    totalRetail: products.reduce((sum, product) => sum + product.units * product.price, 0),
    categories: Array.from(categoryUnits, ([label, units]) => ({ label, units })).filter((item) => item.units > 0).sort((a, b) => b.units - a.units),
    sizes: Array.from(sizeUnits, ([label, units]) => ({ label, units })).filter((item) => item.units > 0).sort((a, b) => b.units - a.units),
    variantRows,
    withImages: products.filter((product) => Boolean(product.image)).length,
  }
}

export function useNisoloProducts(enabled: boolean) {
  const [inventory, setInventory] = useState<NisoloInventory | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || inventory) return

    let cancelled = false
    setLoading(true)
    setError(null)

    fetch(NISOLO_CSV_URL)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load the Nisolo product file.')
        return response.text()
      })
      .then((text) => {
        if (cancelled) return
        const parsed = Papa.parse<CsvRow>(text, { header: true, skipEmptyLines: true })
        setInventory(buildInventory(parsed.data))
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [enabled, inventory])

  return { inventory, loading, error }
}
